import React, { useState, useEffect } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import SearchableSelect from '../components/SearchableSelect';

const ACTION_OPTIONS = [
  { value: '', label: 'All Actions' },
  { value: 'LOGIN', label: 'Login' },
  { value: 'CREATE_EVENT', label: 'Create Event' },
  { value: 'UPDATE_EVENT', label: 'Update Event' },
  { value: 'ISSUE_PASS', label: 'Issue Pass' },
  { value: 'REVOKE_PASS', label: 'Revoked Pass' },
  { value: 'VERIFY_PASS', label: 'Pass Verified' },
  { value: 'PASS_RESCANNED', label: 'Pass Rescanned' },
  { value: 'UPDATE_ROLE', label: 'Update Role' },
  { value: 'ADMIN_UPDATE_USER', label: 'Admin User Update' }
];

export default function AdminLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Filters
  const [actionFilter, setActionFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [usersList, setUsersList] = useState([]);

  const loadLogs = async (pageNum = 1) => {
    setLoading(true);
    try {
      let url = `/logs?page=${pageNum}&limit=50`;
      if (actionFilter) url += `&action=${actionFilter}`;
      if (userFilter) url += `&user=${userFilter}`;
      if (startDate) url += `&startDate=${startDate}`;
      if (endDate) url += `&endDate=${endDate}`;
      
      const res = await api.get(url);
      setLogs(res.data.logs || []);
      setTotalPages(res.data.pages || 1);
      setPage(pageNum);
    } catch (err) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await api.get('/auth/admin/users');
        setUsersList(res.data.users || []);
      } catch (err) {
        console.error('Failed to fetch users for filter');
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    loadLogs(1);
  }, [actionFilter, userFilter, startDate, endDate]);

  const formatDetails = (log) => {
    const { action, details } = log;
    if (!details) return '—';
    if (typeof details === 'string') return details;

    try {
      if (action === 'CREATE_EVENT') {
        return (
          <div style={{ lineHeight: '1.4' }}>
            <strong>Event:</strong> {details.title} <br/>
            <strong>Venue:</strong> {details.venue} <br/>
            <strong>Date:</strong> {details.date ? new Date(details.date).toLocaleDateString('en-IN') : 'N/A'}
          </div>
        );
      }

      if (action === 'UPDATE_EVENT') {
        const fieldMap = {
          title: 'Title',
          date: 'Date',
          venue: 'Venue',
          type: 'Type',
          maxGuests: 'Capacity',
          status: 'Status',
          collaborator: 'Collaborator'
        };

        const renderValue = (field, val) => {
          if (field === 'date') return new Date(val).toLocaleDateString('en-IN');
          return val || '—';
        };

        const changeEntries = details.changes ? Object.entries(details.changes) : [];
        
        return (
          <div style={{ lineHeight: '1.4' }}>
            <strong>Event:</strong> {details.title} <br/>
            {changeEntries.map(([field, delta]) => (
              <div key={field} style={{ marginTop: '0.25rem', fontSize: '0.65rem' }}>
                <span style={{ color: 'var(--text-3)', fontWeight: 600 }}>{fieldMap[field] || field}:</span>{' '}
                <span style={{ color: '#dc2626', textDecoration: 'line-through' }}>{renderValue(field, delta.old)}</span>
                {' → '}
                <span style={{ color: '#059669', fontWeight: 700 }}>{renderValue(field, delta.new)}</span>
              </div>
            ))}
            {changeEntries.length === 0 && details.updates && (
              <div style={{ fontSize: '0.65rem', color: '#005fa7' }}>
                Fields: {details.updates.map(f => fieldMap[f] || f).join(', ')}
              </div>
            )}
          </div>
        );
      }

      if (action === 'ISSUE_PASS') {
        return (
          <div style={{ lineHeight: '1.4' }}>
            <strong>Guest Name:</strong> {details.guestName} <br/>
            <strong>Invite Code:</strong> {details.passCode} <br/>
            <strong>Event Name:</strong> {details.eventName || 'N/A'}
          </div>
        );
      }

      if (action === 'VERIFY_PASS' || action === 'PASS_RESCANNED') {
        const isRescan = action === 'PASS_RESCANNED';
        return (
          <div style={{ lineHeight: '1.4' }}>
            <strong>Guest:</strong> {details.guestName} <br/>
            <strong>Code:</strong> {details.passCode} <br/>
            <span style={{ color: isRescan ? '#d97706' : '#059669', fontWeight: 600 }}>
              {isRescan ? 'RESCANNED' : 'VERIFIED'}
            </span>
          </div>
        );
      }

      if (action === 'LOGIN') {
        return (
          <div style={{ lineHeight: '1.4' }}>
            Logged in via <span style={{ textTransform: 'uppercase', fontWeight: 700, color: '#005fa7' }}>{details.method || 'local'}</span>
          </div>
        );
      }

      if (action === 'UPDATE_ROLE') {
        return (
          <div style={{ lineHeight: '1.4' }}>
            Changed <span style={{ fontWeight: 600 }}>{details.email}</span> role to <span style={{ textTransform: 'uppercase', color: '#005fa7', fontWeight: 700 }}>{details.newRole}</span>
          </div>
        );
      }

      if (action === 'ADMIN_UPDATE_USER') {
        const fieldMap = {
          name: 'Name',
          email: 'Email',
          phone: 'Phone',
          role: 'Role',
          designation: 'Designation'
        };

        const changeEntries = details.changes ? Object.entries(details.changes) : [];
        
        return (
          <div style={{ lineHeight: '1.4' }}>
            <div style={{ fontWeight: 600, color: 'var(--navy)' }}>Updated User: {details.email}</div>
            {changeEntries.map(([field, delta]) => (
              <div key={field} style={{ marginTop: '0.25rem', fontSize: '0.65rem' }}>
                <span style={{ color: 'var(--text-3)', fontWeight: 600 }}>{fieldMap[field] || field}:</span>{' '}
                <span style={{ color: '#dc2626', textDecoration: 'line-through' }}>{delta.old || '—'}</span>
                {' → '}
                <span style={{ color: '#059669', fontWeight: 700 }}>{delta.new || '—'}</span>
              </div>
            ))}
            {changeEntries.length === 0 && <div style={{ fontSize: '0.65rem', color: 'var(--text-3)' }}>No data changed</div>}
          </div>
        );
      }

      if (action === 'REVOKE_PASS') {
        return (
          <div style={{ lineHeight: '1.4' }}>
            <strong>Guest:</strong> {details.guestName} <br/>
            <strong>Reason:</strong> <span style={{ color: '#dc2626' }}>{details.reason || 'No reason'}</span>
          </div>
        );
      }

      // Default fallback for other objects
      return Object.entries(details)
        .map(([k, v]) => `${k}: ${v}`)
        .join(', ');
    } catch (e) {
      return JSON.stringify(details);
    }
  };

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <div className="page-title">Audit Logs</div>
          <div className="page-subtitle">System-wide administrative action history</div>
        </div>
      </div>

      <div className="page-body">
        {/* Filters */}
        <div className="card toolbar" style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ minWidth: '200px' }}>
            <SearchableSelect
              id="action-filter"
              options={ACTION_OPTIONS}
              value={actionFilter}
              onChange={val => setActionFilter(val)}
              placeholder="Search Action..."
            />
          </div>
          <div style={{ minWidth: '250px' }}>
            <SearchableSelect
              id="user-filter"
              options={[
                { value: '', label: 'All Users' },
                ...usersList.map(u => ({ value: u._id, label: `${u.name} (${u.email})` }))
              ]}
              value={userFilter}
              onChange={val => setUserFilter(val)}
              placeholder="Search User..."
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-3)', fontWeight: 600 }}>FROM</span>
            <input type="date" className="toolbar-input" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ height: '3.25rem', borderRadius: '12px' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-3)', fontWeight: 600 }}>TO</span>
            <input type="date" className="toolbar-input" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ height: '3.25rem', borderRadius: '12px' }} />
          </div>
          {(actionFilter || userFilter || startDate || endDate) && (
            <button 
              className="btn btn-ghost btn-sm" 
              onClick={() => { setActionFilter(''); setUserFilter(''); setStartDate(''); setEndDate(''); }}
            >
              Clear
            </button>
          )}
        </div>

        <div className="card">
          {loading && logs.length === 0 ? (
            <div className="page-loading">
              <div className="spinner" />
            </div>
          ) : logs.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">LOGS</div>
              <div className="empty-title">No logs available</div>
              <div className="empty-text">Audit tracking has recently started.</div>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Action</th>
                    <th>Performed By</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log._id}>
                      <td style={{ whiteSpace: 'nowrap', color: 'var(--text-3)', fontSize: 'var(--text-sm)' }}>
                        {new Date(log.createdAt).toLocaleString('en-IN', {
                          day: '2-digit', month: 'short', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </td>
                      <td>
                        <span className="badge badge-active" style={{ fontSize: '0.7rem' }}>
                          {log.action}
                        </span>
                      </td>
                      <td>
                        <div className="cell-stack-title">{log.performedBy?.name || 'System'}</div>
                        <div className="cell-stack-muted">{log.performedBy?.email}</div>
                      </td>
                      <td style={{ fontSize: 'var(--text-xs)', maxWidth: '300px', padding: '0.75rem 1rem' }}>
                        {formatDetails(log)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalPages > 1 && (
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', padding: '1rem' }}>
                  <button 
                    className="btn btn-ghost btn-sm" 
                    disabled={page === 1}
                    onClick={() => loadLogs(page - 1)}
                  >
                    Previous
                  </button>
                  <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-3)', alignSelf: 'center' }}>
                    Page {page} of {totalPages}
                  </span>
                  <button 
                    className="btn btn-ghost btn-sm" 
                    disabled={page === totalPages}
                    onClick={() => loadLogs(page + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
